import React, { ReactNode, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { PickupPoint, getPickupPointId, useGetPickupPointQuery } from '@printdeal/checkout-frontend';
import { Spinner } from '@printdeal/ui-library';
import ErrorAlert from '../error-handling/ErrorAlert';

interface Props {
  errorMessage?: ReactNode;
  href: string;
  showLabel?: boolean;
}

const PickupPointDetails = ({ href, showLabel = true, errorMessage }: Props) => {
  const [pickupPoint, setPickupPoint] = useState<PickupPoint | Error | undefined>(undefined);

  const pickupPointQuery = useGetPickupPointQuery({
    pickupPointId: getPickupPointId(href),
  });

  useEffect(() => {
    if (pickupPointQuery.data) {
      setPickupPoint(pickupPointQuery.data);
    }
  }, [pickupPointQuery.data]);

  const pickupPointLabel = showLabel ? (
    <div>
      <FormattedMessage
        id="pickupPointDetails.pickupPoint"
        defaultMessage="Pickup point"
      />:
    </div>
  ) : '';

  if (!pickupPoint) {
    return (
      <>
        {pickupPointLabel}
        <div>
          <Spinner size="sm" variant="dim" />
        </div>
      </>
    );
  }

  if (pickupPoint instanceof Error) {
    return (
      <>
        {pickupPointLabel}
        <ErrorAlert
          className="pd-m-0"
          scrollIntoView={false}
          showDebugId
          showNextSteps={false}
          sentryException={{
            e: pickupPoint,
            tags: {
              fileLocation: 'PickupPointDetails.tsx',
              featureFunction: 'PickupPointDetails',
            },
            fingerPrint: ['pickup-point-details'],
          }}
        >
          { errorMessage || (
            <FormattedMessage
              id="pickupPointDetails.error"
              defaultMessage="Could not fetch pickup point details. If the problem persists, please try setting a new pickup point or delivery address."
            />
          )}
        </ErrorAlert>
      </>
    );
  }

  return (
    <>
      {pickupPointLabel}
      <div>{pickupPoint.name}</div>
      <div>{pickupPoint.address.addressLine1}</div>
      <div>{pickupPoint.address.postalCode} {pickupPoint.address.city}</div>
    </>
  );
};

export default PickupPointDetails;
